import React from "react";
import {useTranslation} from "react-i18next";
import Container from "@material-ui/core/Container";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import {Grid, ListItem, ListItemIcon, ListItemText} from "@material-ui/core";
import withWidth, {WithWidthProps} from '@material-ui/core/withWidth';
import {getMilestonesByShipmentId} from "../../api/MilestoneEndpoint";
import {RR_STATUS} from "../../constants/RRConstants";
import {useSnackbar} from "notistack";
import Loader from "../core/Loader";
import {Flag} from "@material-ui/icons";
import {InfoCardLine} from "./ShipmentInfoCard";


const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flex: 1,
        },
        nothingFoundRoot: {
            flex: 1,
            padding: 20,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
        },
        caption: {
            fontWeight: "bold",
            marginRight: 5
        },
        value: {
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: '11rem'
        }
    }),
);


export interface IMilestoneProps {
    createdDate: string
    location?: string
    type: string
    reached?: boolean
    user?: string
}



const RoutingPointInfoList: React.FC<{ shipmentId: string | number } & WithWidthProps> = ({shipmentId, width}) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();

    const [milestones, setMilestones] = React.useState<IMilestoneProps[] | undefined>(undefined);

    React.useEffect(() => {
        getMilestonesByShipmentId(shipmentId).then(({data}) => {
            const {status, response} = data;

            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("core.server-error"), {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {
                setMilestones(response);
            }

        });
    }, [shipmentId, enqueueSnackbar, t]);


    if (!milestones) {
        return <Loader/>;
    }

    return <MilestonesList milestones={milestones} width={width}/>
};

export const MilestonesList: React.FC<{ milestones: IMilestoneProps[] } & WithWidthProps> = ({milestones, width}) => {

    const classes = useStyles();

    return <Container>
        <div className={classes.root}>
            <List>
                {milestones && milestones.map((milestone, index) => {
                    return (<ListItem key={index}>
                            <ListItemIcon>
                                <Flag/>
                            </ListItemIcon>
                            <ListItemText
                                primary={<MileStoneItem milestone={milestone} width={width}/>}
                            />
                        </ListItem>
                    );
                })}
            </List>
        </div>
    </Container>
};


const MileStoneItem: React.FC<{ milestone: IMilestoneProps } & WithWidthProps> = ({milestone, width}) => {

    const {t} = useTranslation();

    if (!milestone) {
        return <Loader/>;
    }

    const {createdDate, location, type, user} = milestone;

    const date = createdDate ? /\d{4}-\d{2}-\d{2}/.exec(createdDate) : "";

    const isPublicTracking = window.location.hostname.includes("public-tracking");

    return <React.Fragment>
        <Grid container justifyContent="center"
              alignItems="center">
            <Grid item xs={12}>
                <InfoCardLine property={t("milestone.dateEvent")} value={`${date} (${type})`} width={width}/>
                <InfoCardLine property={t("milestone.location")} value={location} width={width}/>
                {!isPublicTracking && <InfoCardLine property={t("milestone.user")} value={user} width={width}/>}
            </Grid>

        </Grid>

    </React.Fragment>
};

export default withWidth()(RoutingPointInfoList);
